import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Trash2, Edit2, AlertCircle, Tag as TagIcon, Save } from "lucide-react";

const STYLES = [
  { v: "standard", label: "Style A · Standard" },
  { v: "premium", label: "Style B · Premium" },
];

const EMPTY = {
  name: "", style: "standard", material: "",
  colour_options_text: "", production_cost: 0, retail_price: 0,
  active: true, description: "", image_url: "",
};

export default function TagProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/etchery/tag-products");
    setProducts(data.products || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this tag product? Will fail if any order references it.")) return;
    try {
      await api.delete(`/admin/etchery/tag-products/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail));
    }
  };

  const save = async (form) => {
    const payload = {
      name: form.name.trim(),
      style: form.style,
      material: form.material.trim(),
      colour_options: (form.colour_options_text || "").split(",").map((s) => s.trim()).filter(Boolean),
      production_cost: Number(form.production_cost) || 0,
      retail_price: Number(form.retail_price) || 0,
      active: !!form.active,
      description: form.description,
      image_url: form.image_url,
    };
    if (form.id) {
      await api.patch(`/admin/etchery/tag-products/${form.id}`, payload);
    } else {
      await api.post("/admin/etchery/tag-products", payload);
    }
    setEditing(null);
    load();
  };

  return (
    <div data-testid="etchery-tag-products">
      <div className="flex items-center gap-3 mb-6">
        <h2 className="font-serif text-2xl">Plant Tag Products</h2>
        <button onClick={() => setEditing({ ...EMPTY })} className="bloom-btn bloom-btn-primary ml-auto" data-testid="tag-new-btn">
          <Plus size={14} /> New tag
        </button>
      </div>

      {loading ? <Empty msg="Loading…" /> : products.length === 0 ? <Empty msg="No tag products yet." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tag-grid">
          {products.map((p) => {
            const margin = p.retail_price - p.production_cost;
            return (
              <article key={p.id} className={`bloom-card p-5 ${!p.active ? "opacity-60" : ""}`} data-testid={`tag-card-${p.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-12 h-12 border border-[var(--bloom-border)] bg-[var(--bloom-surface)] flex items-center justify-center shrink-0">
                    {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-cover" /> : <TagIcon size={20} strokeWidth={1.5} className="text-[var(--bloom-ink-muted)]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-lg leading-tight">{p.name}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-1">
                      {p.style === "premium" ? "Style B · Premium" : "Style A · Standard"}
                      {!p.active && " · Inactive"}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditing({ ...p, colour_options_text: (p.colour_options || []).join(", ") })} className="p-1.5 hover:bg-[var(--bloom-surface)]" data-testid={`tag-edit-${p.id}`}><Edit2 size={13} /></button>
                    <button onClick={() => remove(p.id)} className="p-1.5 hover:bg-[var(--bloom-surface)] text-[var(--bloom-accent)]" data-testid={`tag-delete-${p.id}`}><Trash2 size={13} /></button>
                  </div>
                </div>
                <div className="text-xs font-serif italic text-[var(--bloom-ink-soft)] mb-3">{p.description || "—"}</div>
                <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-[var(--bloom-border)]">
                  <Stat label="Cost" v={`$${p.production_cost.toFixed(2)}`} />
                  <Stat label="Retail" v={`$${p.retail_price.toFixed(2)}`} />
                  <Stat label="Margin" v={`$${margin.toFixed(2)}`} />
                </div>
                {p.colour_options?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {p.colour_options.map((c) => <span key={c} className="text-[10px] tracking-[0.15em] uppercase border border-[var(--bloom-border)] px-2 py-0.5">{c}</span>)}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      {editing && <TagEditor initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TagEditor({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Name is required.");
    setBusy(true); setErr("");
    try { await onSave(form); } catch (e2) { setErr(formatApiError(e2.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="tag-editor-modal">
      <div className="bg-[var(--bloom-bg)] w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--bloom-border)]">
          <h2 className="font-serif text-2xl">{form.id ? "Edit tag" : "New tag product"}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--bloom-surface)]"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Product name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="bloom-input" autoFocus data-testid="tag-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Style">
              <select value={form.style} onChange={(e) => set("style", e.target.value)} className="bloom-input" data-testid="tag-style">
                {STYLES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Active">
              <label className="inline-flex items-center gap-2 text-sm h-9">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
                <span className="font-serif italic">Available for new orders</span>
              </label>
            </Field>
          </div>
          <Field label="Material"><input value={form.material} onChange={(e) => set("material", e.target.value)} className="bloom-input" placeholder="Birch ply, laser etched" /></Field>
          <Field label="Colour options (comma separated)"><input value={form.colour_options_text} onChange={(e) => set("colour_options_text", e.target.value)} className="bloom-input" placeholder="Natural, Black, White" data-testid="tag-colours" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Production cost (NZD)"><input type="number" step="0.01" value={form.production_cost} onChange={(e) => set("production_cost", e.target.value)} className="bloom-input" data-testid="tag-cost" /></Field>
            <Field label="Retail price (NZD)"><input type="number" step="0.01" value={form.retail_price} onChange={(e) => set("retail_price", e.target.value)} className="bloom-input" data-testid="tag-price" /></Field>
          </div>
          <Field label="Description"><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="bloom-input" /></Field>
          <Field label="Image URL"><input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="bloom-input" placeholder="https://… (paste image URL)" /></Field>
          {err && <div className="flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic"><AlertCircle size={14} /> {err}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="bloom-btn bloom-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} className="bloom-btn bloom-btn-primary" data-testid="tag-save"><Save size={12} /> {busy ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><div className="bloom-label mb-2">{label}</div>{children}</div>;
}
function Stat({ label, v }) {
  return <div><div className="text-[9px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)]">{label}</div><div className="font-serif text-sm mt-0.5">{v}</div></div>;
}
function Empty({ msg }) { return <div className="bloom-card p-14 text-center font-serif italic text-[var(--bloom-ink-muted)]">{msg}</div>; }
